<template>
  <div class="flex flex-col gap-6">

    <!-- Header -->
    <AppPageHeader :title="$t('lateArrivalDebts.title')" :description="$t('lateArrivalDebts.description')">
      <template #actions>
        <AppButton variant="outline" :disabled="loading" @click="exportReport">
          <Download class="h-4 w-4 mr-2" />
          {{ $t('lateArrivalDebts.export') }}
        </AppButton>
        <AppButton variant="outline" :disabled="loading" @click="loadData">
          <RotateCcw :class="['h-4 w-4 mr-2', loading ? 'animate-spin' : '']" />
          {{ $t('lateArrivalDebts.refresh') }}
        </AppButton>
      </template>
    </AppPageHeader>

    <!-- Summary stat cards -->
    <div v-if="summary" class="grid grid-cols-2 lg:grid-cols-5 gap-4">
      <AppStatCard :label="$t('lateArrivalDebts.totalOccurrences')" :value="summary.totalOccurrences || 0" :icon="AlarmClock" iconBg="bg-red-50" iconColor="text-red-600" />
      <AppStatCard :label="$t('lateArrivalDebts.employeesAffected')" :value="summary.employeesAffected || 0" :icon="Users" iconBg="bg-amber-50" iconColor="text-amber-600" />
      <AppStatCard :label="$t('lateArrivalDebts.totalHoursLate')" :value="summary.totalHoursLate || '0.00'" :sub="`${summary.totalMinutesLate || 0} ${$t('common.minutes')}`" :icon="Clock" iconBg="bg-purple-50" iconColor="text-purple-600" />
      <AppStatCard :label="$t('lateArrivalDebts.avgMinutesLate')" :value="summary.avgMinutesLate || '0'" :sub="$t('lateArrivalDebts.perOccurrence')" :icon="TrendingDown" iconBg="bg-yellow-50" iconColor="text-yellow-600" />
      <AppStatCard :label="$t('lateArrivalDebts.excused')" :value="summary.excused || 0" :sub="`${$t('lateArrivalDebts.of')} ${summary.totalOccurrences || 0} ${$t('common.total')}`" :icon="CheckCircle" iconBg="bg-emerald-50" iconColor="text-emerald-600" />
    </div>

    <!-- Filters -->
    <AppCard>
      <div class="p-4">
        <div class="flex flex-wrap gap-3 items-end">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-muted-foreground">{{ $t('lateArrivalDebts.startDate') }}</label>
            <input type="date" v-model="filters.startDate" @change="loadData" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-muted-foreground">{{ $t('lateArrivalDebts.endDate') }}</label>
            <input type="date" v-model="filters.endDate" @change="loadData" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-muted-foreground">{{ $t('lateArrivalDebts.status') }}</label>
            <select v-model="filters.status" @change="loadData" class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
              <option value="all">{{ $t('lateArrivalDebts.allStatuses') }}</option>
              <option value="auto_approved">{{ $t('lateArrivalDebts.autoApproved') }}</option>
              <option value="excused">{{ $t('lateArrivalDebts.excused') }}</option>
              <option value="pending_review">{{ $t('lateArrivalDebts.pendingReview') }}</option>
            </select>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-muted-foreground">{{ $t('lateArrivalDebts.search') }}</label>
            <input type="text" v-model="filters.search" @input="debounceSearch" :placeholder="$t('lateArrivalDebts.searchPlaceholder')" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring w-48" />
          </div>
          <AppButton variant="outline" @click="resetFilters">{{ $t('lateArrivalDebts.reset') }}</AppButton>
        </div>
      </div>
    </AppCard>

    <!-- Top offenders panels -->
    <div v-if="summary && summary.topOffenders && summary.topOffenders.length > 0" class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <AppCard>
        <div class="p-4">
          <p class="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{{ $t('lateArrivalDebts.topOffenders') }}</p>
          <div class="space-y-2">
            <div v-for="(emp, i) in summary.topOffenders.slice(0, 5)" :key="emp.employeeId" class="flex items-center justify-between p-3 rounded-lg bg-muted/40">
              <div class="flex items-center gap-3">
                <span class="h-7 w-7 rounded-full bg-red-100 text-red-600 text-xs font-bold flex items-center justify-center shrink-0">{{ i + 1 }}</span>
                <div>
                  <p class="text-sm font-medium text-foreground">{{ emp.name }}</p>
                  <p class="text-xs text-muted-foreground">{{ emp.employeeCode }} · {{ emp.department }}</p>
                </div>
              </div>
              <div class="text-right">
                <p class="text-base font-bold text-red-600">{{ emp.lateCount }}</p>
                <p class="text-xs text-muted-foreground">{{ emp.totalHoursLate }}{{ $t('lateArrivalDebts.totalHours') }}</p>
              </div>
            </div>
          </div>
        </div>
      </AppCard>

      <AppCard>
        <div class="p-4">
          <p class="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{{ $t('lateArrivalDebts.byDepartment') }}</p>
          <div class="space-y-2">
            <div v-for="dept in summary.byDepartment" :key="dept.department" class="flex items-center justify-between p-3 rounded-lg bg-muted/40">
              <div>
                <p class="text-sm font-medium text-foreground">{{ dept.department || 'Unknown' }}</p>
                <p class="text-xs text-muted-foreground">{{ dept.employeesAffected }} {{ $t('lateArrivalDebts.employeesAffectedLabel') }}</p>
              </div>
              <div class="text-right">
                <p class="text-base font-bold text-amber-600">{{ dept.lateCount }}</p>
                <p class="text-xs text-muted-foreground">{{ dept.totalHoursLate }}h</p>
              </div>
            </div>
          </div>
        </div>
      </AppCard>
    </div>

    <!-- Main table -->
    <AppCard>
      <AppTable
        :columns="i18nTableColumns"
        :rows="debts"
        :loading="loading"
        rowKey="id"
        :emptyText="$t('lateArrivalDebts.noLateArrivals')"
      >
        <template #cell-date="{ row }">
          <span class="text-sm text-foreground">{{ formatDate(row.date) }}</span>
        </template>

        <template #cell-employee="{ row }">
          <div class="flex items-center gap-2.5">
            <div class="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold shrink-0">
              {{ (row.employeeName || '?').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() }}
            </div>
            <div>
              <p class="text-sm font-medium text-foreground">{{ row.employeeName }}</p>
              <p class="text-xs text-muted-foreground">{{ row.employeeCode }}</p>
            </div>
          </div>
        </template>

        <template #cell-department="{ row }">
          <span class="text-sm text-foreground">{{ row.department || 'N/A' }}</span>
        </template>

        <template #cell-clockIn="{ row }">
          <div>
            <p class="text-sm font-mono text-foreground">{{ row.actualClockInTime }}</p>
            <p class="text-xs text-muted-foreground">{{ $t('lateArrivalDebts.grace') }}: {{ row.gracePeriodEnd }}</p>
          </div>
        </template>

        <template #cell-lateDuration="{ row }">
          <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold" :class="getLatenessSeverityClass(row.lateDurationMinutes)">
            {{ row.lateDurationMinutes }}m
            <span class="font-normal opacity-70">({{ (row.lateDurationMinutes / 60).toFixed(2) }}h)</span>
          </span>
        </template>

        <template #cell-status="{ row }">
          <div>
            <AppBadge :variant="row.status === 'excused' ? 'success' : row.status === 'pending_review' ? 'default' : 'warning'">
              {{ formatStatus(row.status) }}
            </AppBadge>
            <p v-if="row.excusedBy" class="text-xs text-muted-foreground mt-0.5">{{ $t('lateArrivalDebts.by') }} {{ row.excusedBy }}</p>
          </div>
        </template>

        <template #actions="{ row }">
          <div class="flex items-center gap-1">
            <AppButton v-if="row.status !== 'excused'" size="icon-sm" variant="ghost" class="text-emerald-600 hover:text-emerald-700" :title="$t('lateArrivalDebts.excuse')" @click.stop="openExcuseModal(row)">
              <CheckCircle class="h-3.5 w-3.5" />
            </AppButton>
            <AppButton v-if="row.status === 'excused'" size="icon-sm" variant="ghost" class="text-amber-600 hover:text-amber-700" :title="$t('lateArrivalDebts.removeExcuse')" @click.stop="removeExcuse(row.id)">
              <XCircle class="h-3.5 w-3.5" />
            </AppButton>
            <AppButton size="icon-sm" variant="ghost" :title="$t('lateArrivalDebts.viewDetails')" @click.stop="viewDetails(row)">
              <Eye class="h-3.5 w-3.5" />
            </AppButton>
            <AppButton size="icon-sm" variant="ghost" class="text-destructive hover:text-destructive" :title="$t('lateArrivalDebts.delete')" @click.stop="confirmDelete(row)">
              <Trash2 class="h-3.5 w-3.5" />
            </AppButton>
          </div>
        </template>
      </AppTable>

      <!-- Pagination -->
      <div v-if="pagination && pagination.totalPages > 1" class="flex items-center justify-between px-4 py-3 border-t border-border">
        <span class="text-sm text-muted-foreground">{{ $t('common.page') }} {{ pagination.page }} {{ $t('common.of') }} {{ pagination.totalPages }} ({{ pagination.total }} {{ $t('common.total') }})</span>
        <div class="flex items-center gap-1">
          <AppButton size="icon-sm" variant="outline" :disabled="pagination.page === 1" @click="changePage(pagination.page - 1)">
            <ChevronLeft class="h-3.5 w-3.5" />
          </AppButton>
          <AppButton size="icon-sm" variant="outline" :disabled="pagination.page === pagination.totalPages" @click="changePage(pagination.page + 1)">
            <ChevronRight class="h-3.5 w-3.5" />
          </AppButton>
        </div>
      </div>
    </AppCard>

    <!-- Excuse modal -->
    <AppModal v-model="showExcuseModal" :title="$t('lateArrivalDebts.excuseLateArrival')" size="sm">
      <div v-if="selectedDebt" class="space-y-4">
        <div class="rounded-lg bg-muted/50 p-3 text-sm space-y-1">
          <p><span class="text-muted-foreground">{{ $t('lateArrivalDebts.employee') }}:</span> <strong>{{ selectedDebt.employeeName }}</strong></p>
          <p><span class="text-muted-foreground">{{ $t('lateArrivalDebts.date') }}:</span> {{ formatDate(selectedDebt.date) }}</p>
          <p><span class="text-muted-foreground">{{ $t('lateArrivalDebts.lateDuration') }}:</span> {{ selectedDebt.lateDurationMinutes }} {{ $t('lateArrivalDebts.minutes') }}</p>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">{{ $t('lateArrivalDebts.excuseReasonRequired') }}</label>
          <textarea v-model="excuseForm.excuseReason" rows="3" class="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none" :placeholder="$t('lateArrivalDebts.excuseReason') + '…'"></textarea>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">{{ $t('lateArrivalDebts.additionalNotes') }}</label>
          <textarea v-model="excuseForm.notes" rows="2" class="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none" :placeholder="$t('lateArrivalDebts.notesPlaceholder')"></textarea>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton variant="outline" @click="closeExcuseModal">{{ $t('lateArrivalDebts.cancel') }}</AppButton>
          <AppButton :disabled="!excuseForm.excuseReason" variant="success" @click="submitExcuse">{{ $t('lateArrivalDebts.submitExcuse') }}</AppButton>
        </div>
      </template>
    </AppModal>

    <!-- Details modal -->
    <AppModal v-model="showDetailsModal" :title="$t('lateArrivalDebts.lateArrivalDetails')" size="lg">
      <div v-if="selectedDebt" class="space-y-4">
        <div class="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p class="text-xs text-muted-foreground mb-0.5">{{ $t('lateArrivalDebts.employee') }}</p>
            <p class="font-medium text-foreground">{{ selectedDebt.employeeName }} ({{ selectedDebt.employeeCode }})</p>
          </div>
          <div>
            <p class="text-xs text-muted-foreground mb-0.5">{{ $t('lateArrivalDebts.department') }}</p>
            <p class="font-medium text-foreground">{{ selectedDebt.department || 'N/A' }}</p>
          </div>
          <div>
            <p class="text-xs text-muted-foreground mb-0.5">{{ $t('lateArrivalDebts.date') }}</p>
            <p class="font-medium text-foreground">{{ formatDate(selectedDebt.date) }}</p>
          </div>
          <div>
            <p class="text-xs text-muted-foreground mb-0.5">{{ $t('lateArrivalDebts.status') }}</p>
            <AppBadge :variant="selectedDebt.status === 'excused' ? 'success' : selectedDebt.status === 'pending_review' ? 'default' : 'warning'">
              {{ formatStatus(selectedDebt.status) }}
            </AppBadge>
          </div>
        </div>

        <div class="rounded-lg border border-border p-4">
          <p class="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{{ $t('lateArrivalDebts.timeDetails') }}</p>
          <div class="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p class="text-xs text-muted-foreground mb-0.5">{{ $t('lateArrivalDebts.scheduledStart') }}</p>
              <p class="font-medium text-foreground">{{ selectedDebt.scheduledStartTime }}</p>
            </div>
            <div>
              <p class="text-xs text-muted-foreground mb-0.5">{{ $t('lateArrivalDebts.gracePeriodEnd') }}</p>
              <p class="font-medium text-foreground">{{ selectedDebt.gracePeriodEnd }}</p>
            </div>
            <div>
              <p class="text-xs text-muted-foreground mb-0.5">{{ $t('lateArrivalDebts.actualClockIn') }}</p>
              <p class="font-bold text-destructive">{{ selectedDebt.actualClockInTime }}</p>
            </div>
          </div>
        </div>

        <div class="rounded-lg bg-red-50 border border-red-200 p-4">
          <p class="text-xs font-semibold text-red-700 uppercase tracking-wider mb-1">{{ $t('lateArrivalDebts.lateDurationTitle') }}</p>
          <p class="text-2xl font-bold text-red-600">
            {{ selectedDebt.lateDurationMinutes }} {{ $t('lateArrivalDebts.minutes') }}
            <span class="text-sm font-normal text-muted-foreground">({{ (selectedDebt.lateDurationMinutes / 60).toFixed(2) }}h)</span>
          </p>
        </div>

        <div v-if="selectedDebt.status === 'excused'" class="rounded-lg bg-emerald-50 border border-emerald-200 p-4 space-y-2 text-sm">
          <p class="text-xs font-semibold text-emerald-700 uppercase tracking-wider">{{ $t('lateArrivalDebts.excuseInfo') }}</p>
          <div>
            <p class="text-xs text-muted-foreground">{{ $t('lateArrivalDebts.excuseReasonLabel') }}</p>
            <p class="text-foreground">{{ selectedDebt.excuseReason || 'N/A' }}</p>
          </div>
          <div v-if="selectedDebt.excusedBy">
            <p class="text-xs text-muted-foreground">{{ $t('lateArrivalDebts.excusedBy') }}</p>
            <p class="text-foreground">{{ selectedDebt.excusedBy }}</p>
          </div>
          <div v-if="selectedDebt.excusedAt">
            <p class="text-xs text-muted-foreground">{{ $t('lateArrivalDebts.excusedAt') }}</p>
            <p class="text-foreground">{{ selectedDebt.excusedAt }}</p>
          </div>
        </div>

        <div v-if="selectedDebt.notes" class="text-sm">
          <p class="text-xs text-muted-foreground mb-1">{{ $t('lateArrivalDebts.notes') }}</p>
          <p class="text-foreground">{{ selectedDebt.notes }}</p>
        </div>
      </div>
    </AppModal>

  </div>
</template>

<script>
import { AlarmClock, Users, Clock, TrendingDown, CheckCircle, XCircle, Eye, Trash2, ChevronLeft, ChevronRight, Download, RotateCcw } from 'lucide-vue-next';
import { AppPageHeader, AppStatCard, AppCard, AppTable, AppBadge, AppButton, AppModal } from '@/components/ui';
import lateArrivalDebtsService from '@/services/lateArrivalDebtsService';

export default {
  name: 'LateArrivalDebts',

  components: {
    AppPageHeader, AppStatCard, AppCard, AppTable, AppBadge, AppButton, AppModal,
    AlarmClock, Users, Clock, TrendingDown, CheckCircle, XCircle, Eye, Trash2,
    ChevronLeft, ChevronRight, Download, RotateCcw,
  },

  data() {
    return {
      loading: false,
      debts: [],
      summary: null,
      pagination: null,
      tableColumns: [],
      filters: {
        startDate: '',
        endDate: '',
        status: 'all',
        search: '',
        page: 1,
        limit: 50
      },
      showExcuseModal: false,
      showDetailsModal: false,
      selectedDebt: null,
      excuseForm: {
        excuseReason: '',
        notes: ''
      },
      searchTimeout: null
    };
  },

  computed: {
    i18nTableColumns() {
      return [
        { key: 'date',        label: this.$t('lateArrivalDebts.date') },
        { key: 'employee',    label: this.$t('lateArrivalDebts.employee') },
        { key: 'department',  label: this.$t('lateArrivalDebts.department'), mobileHide: true },
        { key: 'clockIn',     label: this.$t('lateArrivalDebts.clockIn'), mobileHide: true },
        { key: 'lateDuration',label: this.$t('lateArrivalDebts.lateDuration') },
        { key: 'status',      label: this.$t('lateArrivalDebts.status') },
      ];
    },
  },

  mounted() {
    this.setDefaultDates();
    this.loadData();
  },

  methods: {
    setDefaultDates() {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

      this.filters.endDate = today.toISOString().split('T')[0];
      this.filters.startDate = firstDay.toISOString().split('T')[0];
    },

    async loadData() {
      this.loading = true;
      try {
        // Load debts
        const debtsResponse = await lateArrivalDebtsService.getLateArrivalDebts({
          page: this.filters.page,
          limit: this.filters.limit,
          startDate: this.filters.startDate,
          endDate: this.filters.endDate,
          status: this.filters.status,
          search: this.filters.search
        });

        if (debtsResponse.success) {
          this.debts = debtsResponse.data.debts;
          this.pagination = debtsResponse.data.pagination;
        }

        // Load summary
        const summaryResponse = await lateArrivalDebtsService.getSummary({
          startDate: this.filters.startDate,
          endDate: this.filters.endDate
        });

        if (summaryResponse.success) {
          this.summary = summaryResponse.data.summary;
          this.summary.topOffenders = summaryResponse.data.topOffenders;
          this.summary.byDepartment = summaryResponse.data.byDepartment;
        }
      } catch (error) {
        console.error('Load data error:', error);
        this.$toast?.error('Failed to load late arrival data');
      } finally {
        this.loading = false;
      }
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.loadData();
      }, 500);
    },

    resetFilters() {
      this.setDefaultDates();
      this.filters.status = 'all';
      this.filters.search = '';
      this.filters.page = 1;
      this.loadData();
    },

    changePage(page) {
      this.filters.page = page;
      this.loadData();
    },

    openExcuseModal(debt) {
      this.selectedDebt = debt;
      this.excuseForm = {
        excuseReason: '',
        notes: ''
      };
      this.showExcuseModal = true;
    },

    closeExcuseModal() {
      this.showExcuseModal = false;
      this.selectedDebt = null;
      this.excuseForm = {
        excuseReason: '',
        notes: ''
      };
    },

    async submitExcuse() {
      try {
        await lateArrivalDebtsService.excuseLateArrival(this.selectedDebt.id, this.excuseForm);
        this.$toast?.success('Late arrival excused successfully');
        this.closeExcuseModal();
        this.loadData();
      } catch (error) {
        console.error('Excuse error:', error);
        this.$toast?.error('Failed to excuse late arrival');
      }
    },

    async removeExcuse(id) {
      if (!confirm('Are you sure you want to remove the excuse from this late arrival?')) {
        return;
      }

      try {
        await lateArrivalDebtsService.removeLateArrivalExcuse(id);
        this.$toast?.success('Excuse removed successfully');
        this.loadData();
      } catch (error) {
        console.error('Remove excuse error:', error);
        this.$toast?.error('Failed to remove excuse');
      }
    },

    viewDetails(debt) {
      this.selectedDebt = debt;
      this.showDetailsModal = true;
    },

    async confirmDelete(debt) {
      if (!confirm(`Are you sure you want to delete this late arrival record for ${debt.employeeName}?`)) {
        return;
      }

      try {
        await lateArrivalDebtsService.deleteLateArrival(debt.id);
        this.$toast?.success('Late arrival record deleted successfully');
        this.loadData();
      } catch (error) {
        console.error('Delete error:', error);
        this.$toast?.error('Failed to delete late arrival record');
      }
    },

    async exportReport() {
      try {
        await lateArrivalDebtsService.exportLateArrivals({
          startDate: this.filters.startDate,
          endDate: this.filters.endDate,
          status: this.filters.status
        });
        this.$toast?.success('Report exported successfully');
      } catch (error) {
        console.error('Export error:', error);
        this.$toast?.error('Failed to export report');
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    },

    formatStatus(status) {
      const statusMap = {
        'auto_approved': 'Auto Approved',
        'excused': 'Excused',
        'pending_review': 'Pending Review'
      };
      return statusMap[status] || status;
    },

    getStatusClass(status) {
      const classMap = {
        'auto_approved': 'bg-yellow-100 text-yellow-800',
        'excused': 'bg-green-100 text-green-800',
        'pending_review': 'bg-blue-100 text-blue-800'
      };
      return classMap[status] || 'bg-gray-100 text-gray-800';
    },

    getLatenessSeverityClass(minutes) {
      if (minutes <= 15) return 'bg-yellow-100 text-yellow-800';
      if (minutes <= 30) return 'bg-orange-100 text-orange-800';
      return 'bg-red-100 text-red-800';
    }
  }
};
</script>
